import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircleMore } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAgentPageByUrlName } from "@/hooks/agent/useAgentPage";
import { useBrain } from "@/contexts/ui/brain";

const TUTOR_URL_NAME = "help-genie-tutor";
const MASCOT_SRC = "/genie-mascots/role/support.png";

const TutorGeniePage = () => {
  const navigate = useNavigate();
  const { openBrain } = useBrain();
  const { data: tutorAgent, isLoading } = useAgentPageByUrlName(TUTOR_URL_NAME);

  return (
    <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden flex flex-col">
      <div className="container mx-auto px-4 pt-6 pb-3 max-w-[1920px]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => navigate("/learn")}
            className="-ml-3 text-gray-600 hover:text-genie-primary hover:bg-genie-primary/5"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Learn
          </Button>

          <div className="flex items-center gap-3">
            <img src={MASCOT_SRC} alt="Help Genie mascot" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-genie-text">Help Genie Tutor</div>
              <div className="text-xs text-gray-500">Your in-app guide to the platform</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 container mx-auto px-4 pb-6 max-w-[1920px]">
        {isLoading ? (
          <div className="w-full h-[calc(100vh-160px)] min-h-[560px] rounded-2xl border-2 border-genie-primary/20 bg-white flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-genie-primary" />
          </div>
        ) : tutorAgent ? (
          <div className="w-full h-[calc(100vh-160px)] min-h-[560px] rounded-2xl overflow-hidden border-2 border-genie-primary/20 bg-white shadow-sm">
            <iframe
              src={`/live-agent/${TUTOR_URL_NAME}?embed=true`}
              title="Help Genie Tutor"
              className="w-full h-full"
              frameBorder={0}
              allow="microphone; autoplay; clipboard-read; clipboard-write"
            />
          </div>
        ) : (
          <div className="w-full rounded-2xl border-2 border-genie-primary/20 bg-gradient-to-br from-genie-primary/5 via-white to-genie-secondary/5 p-8 sm:p-12">
            <div className="max-w-2xl mx-auto text-center">
              <img
                src={MASCOT_SRC}
                alt="Help Genie mascot"
                className="w-24 h-24 sm:w-32 sm:h-32 object-contain mx-auto mb-6"
              />
              <h2 className="text-2xl sm:text-3xl font-bold text-genie-text mb-3">
                Tutor Genie not set up yet
              </h2>
              <p className="text-gray-600 mb-6">
                Until a Genie with the URL <code className="px-2 py-0.5 rounded bg-white border border-gray-200 text-genie-primary font-mono text-sm">{TUTOR_URL_NAME}</code> is published in this account, you can still get help from the <span className="font-medium text-genie-primary">Your Genie</span> sidebar — it's the platform's built-in tutor.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => openBrain()}
                  className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30"
                >
                  <MessageCircleMore className="h-4 w-4 mr-2" />
                  Open Your Genie
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/user-hub/genie-setup")}
                  className="border-genie-primary/30 text-genie-primary hover:bg-genie-primary/5"
                >
                  Set up the Tutor Genie
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorGeniePage;
